import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

export interface SectionConfig {
  id: string;
  label: string;
  group: string; // maps to cmsFieldsConfig group
  visible: boolean;
}

interface EditModeContextType {
  isEditMode: boolean;
  setEditMode: (v: boolean) => void;
  selectedSection: string | null;
  selectSection: (id: string | null) => void;
  sectionOrder: SectionConfig[];
  setSectionOrder: (order: SectionConfig[]) => void;
  saveSectionOrder: (order: SectionConfig[]) => Promise<void>;
  panelOpen: boolean;
  setPanelOpen: (v: boolean) => void;
}

const EditModeContext = createContext<EditModeContextType>({
  isEditMode: false,
  setEditMode: () => {},
  selectedSection: null,
  selectSection: () => {},
  sectionOrder: [],
  setSectionOrder: () => {},
  saveSectionOrder: async () => {},
  panelOpen: false,
  setPanelOpen: () => {},
});

export const useEditMode = () => useContext(EditModeContext);

const DEFAULT_SECTIONS: SectionConfig[] = [
  { id: "hero", label: "Hero", group: "Hero", visible: true },
  { id: "booking", label: "Agendamento", group: "Agendamento", visible: true },
  { id: "services", label: "Serviços", group: "Serviços", visible: true },
  { id: "about", label: "Sobre Mim", group: "Sobre Mim", visible: true },
  { id: "testimonials", label: "Testemunhos", group: "Testemunhos", visible: true },
  { id: "contact", label: "Contacto", group: "Contacto", visible: true },
];

export const EditModeProvider = ({ children }: { children: ReactNode }) => {
  const { isAdmin } = useAuth();
  const [isEditMode, setEditModeState] = useState(false);
  const [selectedSection, setSelectedSection] = useState<string | null>(null);
  const [sectionOrder, setSectionOrder] = useState<SectionConfig[]>(DEFAULT_SECTIONS);
  const [panelOpen, setPanelOpen] = useState(false);

  // Activate edit mode from URL param
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("edit") === "true" && isAdmin) {
      setEditModeState(true);
    }
  }, [isAdmin]);

  // Load section order from DB
  useEffect(() => {
    supabase
      .from("app_settings")
      .select("value")
      .eq("key", "section_order")
      .maybeSingle()
      .then(({ data }) => {
        if (data?.value) {
          try {
            const parsed = typeof data.value === "string" ? JSON.parse(data.value) : data.value;
            if (Array.isArray(parsed) && parsed.length > 0) {
              setSectionOrder(parsed);
            }
          } catch {}
        }
      });
  }, []);

  const setEditMode = useCallback((v: boolean) => {
    if (v && !isAdmin) return;
    setEditModeState(v);
    if (!v) {
      setSelectedSection(null);
      setPanelOpen(false);
    }
  }, [isAdmin]);

  const selectSection = useCallback((id: string | null) => {
    setSelectedSection(id);
    setPanelOpen(!!id);
  }, []);

  const saveSectionOrder = useCallback(async (order: SectionConfig[]) => {
    setSectionOrder(order);
    await supabase
      .from("app_settings")
      .upsert({ key: "section_order", value: JSON.stringify(order) }, { onConflict: "key" });
  }, []);

  return (
    <EditModeContext.Provider
      value={{
        isEditMode,
        setEditMode,
        selectedSection,
        selectSection,
        sectionOrder,
        setSectionOrder,
        saveSectionOrder,
        panelOpen,
        setPanelOpen,
      }}
    >
      {children}
    </EditModeContext.Provider>
  );
};
